import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExportLink } from "@/components/ui/data-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { exportHref } from "@jbnl/api-client";
import { cn, Toolbar } from "@jbnl/shared-ui";
import type { PlanningDag } from "./usePlanning";

/**
 * Every baan and every korps, which is what the boekje prints and why the API crosses a
 * default-deny filter. One dag at a time: the season end to end was a page nobody scrolled.
 */
export function Speelschema({
  seizoenId,
  dag,
  eigen,
}: {
  seizoenId: string;
  dag: PlanningDag;
  /** The korps ids the reader may act for, so their own rows can be marked. */
  eigen: ReadonlySet<string>;
}) {
  const { t } = useTranslation(["planning"]);

  // Two columns only where there are two banen to put in them: a lone baan stretched across
  // half the page with white beside it reads as a column that failed to fill, not as a layout.
  const kolommen = dag.banen.length > 1 ? "lg:grid-cols-2" : "";

  return (
    <div className="space-y-6">
      <Toolbar>
        <ExportLink
          href={exportHref(
            "/api/seizoenen/{seizoenId}/planning/wedstrijddagen/{wedstrijddagId}/export",
            { path: { seizoenId, wedstrijddagId: dag.id } },
          )}
        />
      </Toolbar>

      {/* Side by side, as the boekje's spread is: stacked full-width, a dag ran several screens
        with most of every row empty. `items-start`, because banen hold different numbers of
        starttijden and levelling them pads the short one with a void. */}
      <div className={cn("grid items-start gap-6", kolommen)}>
        {dag.banen.map((baan) => (
          <Card key={baan.id}>
            <CardHeader>
              {/* Named for the same reason the juryleden table below is - it is the baan's label
                  that tells the two tables in this card apart. */}
              <CardTitle id={`baan-${baan.id}`}>{baan.label}</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <Table aria-labelledby={`baan-${baan.id}`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">
                        {t("planning:dag.starttijd")}
                      </TableHead>
                      <TableHead>{t("planning:dag.ploeg")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {baan.regels.map((regel) => (
                      <Speelschemaregel
                        key={regel.id}
                        regel={regel}
                        eigen={eigen}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>

              {baan.bezetting.length > 0 && (
                <div className="space-y-2">
                  {/* Two tables in one card, so the heading names each: "table" alone says nothing. */}
                  <h4
                    id={`juryleden-${baan.id}`}
                    className="text-sm font-semibold"
                  >
                    {t("planning:bezetting.title")}
                  </h4>
                  <Bezettingstabel
                    bezetting={baan.bezetting}
                    labelledBy={`juryleden-${baan.id}`}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* The rekenkamer's rather than a jurylid's: the job serves the whole dag, and its own
        card says so without a sentence. */}
      {dag.dagbezetting.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle id={`dagbezetting-${dag.id}`}>
              {t("planning:bezetting.dagTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Bezettingstabel
              bezetting={dag.dagbezetting}
              labelledBy={`dagbezetting-${dag.id}`}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * One regel: whether it is the reader's own ploeg and whether it is a pauze both decide the
 * row's colour and its cell content, so both live here rather than inline in the baan's table.
 */
function Speelschemaregel({
  regel,
  eigen,
}: {
  regel: PlanningDag["banen"][number]["regels"][number];
  eigen: ReadonlySet<string>;
}) {
  const { t } = useTranslation(["planning"]);
  const vanMij = regel.korpsId != null && eigen.has(regel.korpsId);
  // A labelled regel is a pauze and never carries a ploeg (ADR-0039), so it
  // is never also a row of the reader's own - the two tints cannot meet.
  const pauze = regel.label !== null;

  return (
    <TableRow
      // Never colour alone: the badge beside the ploeg says the same thing
      // in words, so the row still answers for a reader who does not see
      // the tint. The pauze is the backoffice's muted field, so a korps that
      // reads both screens sees one thing rather than learning a second
      // colour here.
      className={cn(
        vanMij && "bg-secondary-surface hover:bg-secondary-surface",
        pauze && "bg-muted hover:bg-muted",
      )}
    >
      <TableCell className={cn(pauze && "text-muted-foreground")}>
        {regel.starttijd.slice(0, 5)}
      </TableCell>
      <TableCell>
        <span className="flex flex-wrap items-center gap-2">
          {/* Italic marks it as not-a-ploeg without spending a second colour. */}
          {regel.label !== null ? (
            <span className="italic text-muted-foreground">{regel.label}</span>
          ) : (
            (regel.ploegNaam ?? (
              <span className="text-muted-foreground">
                {t("planning:dag.leeg")}
              </span>
            ))
          )}
          {vanMij && <Badge>{t("planning:eigen.badge")}</Badge>}
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * A table, not a run-on line: a korps reads down it looking for who its AAR is, not for the
 * eleventh name in a sentence. One component for both, since only the functies differ.
 */
function Bezettingstabel({
  bezetting,
  labelledBy,
}: {
  bezetting: PlanningDag["dagbezetting"];
  /** The id of the heading above, so the table is announced by name rather than as "table". */
  labelledBy: string;
}) {
  const { t } = useTranslation(["planning"]);

  return (
    <div className="overflow-x-auto">
      <Table aria-labelledby={labelledBy}>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/2">
              {t("planning:bezetting.functie")}
            </TableHead>
            <TableHead>{t("planning:bezetting.persoon")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bezetting.map((b) => (
            // The functie is the key: `Baanfuncties` and `Dagfuncties` are both unique on
            // (planning, baan-or-dag, functie), so neither ever carries the same one twice - a
            // job needing two people is two separately named functies.
            <TableRow key={b.functieNaam}>
              <TableCell>{b.functieNaam}</TableCell>
              <TableCell>{b.persoonNaam}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
